import { debounce } from 'lodash-es'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { change, formValueSelector, initialize, reduxForm } from 'redux-form'

import { DELIVERY_OPTION } from '@cinch/oxd-containers/delivery-preference/src'
import { updateProfile } from '@cinch/oxd-redux/profile/src/actions'
import { getDelivery, getLocation, getMoney } from '@cinch/oxd-redux/profile/src/selectors'
import { getFullListings } from '@cinch/oxd-redux/dealer-vehicles/src/selectors'
import { actions as vehicleActions } from '@cinch/oxd-redux/dealer-vehicles/src'
import { getAllUrlParams } from '@cinch/oxd-utils/browser-support/src'
import { MakesModels } from '@cinch/oxd-utils/filters/src'
import { getModel } from '@cinch/oxd-utils/manufacturers/src'

import ListingFiltersWrapper from './listing-filters-wrapper.component'

export const FORM_NAME = 'filtersDealer'

export interface FiltersPropsFromDispatch {
  updateSortCriteria: (value: number) => void
  updateUserProfile: (values: { [key: string]: any }) => void
  resetMoney: () => void
  resetLocation: () => void
  resetDelivery: () => void
  resetDistance: () => void
  clearFilters: () => void
}

export const defaultValues: Partial<Cinch.FilterFormData> = {
  fromPrice: -1,
  toPrice: -1,
  fromYear: -1,
  toYear: -1,
  mileage: -1,
  fromEngineSize: -1,
  toEngineSize: -1,
  longitude: '',
  latitude: '',
  distance: 0,
  bodyType: '',
  colour: '',
  doors: '',
  fuelType: '',
  keywords: '',
  make: '',
  selectedModel: '',
  transmissionType: '',
  deliveryOption: DELIVERY_OPTION.DEALERSHIP,
}

export const initialValues: Partial<Cinch.FilterFormData> = {
  ...defaultValues,
  dealershipStatus: 'Live',
  pageSize: 24,
  sortingCriteria: 1,
  query: '',
  location: '',
  town: '',
  postcode: '',
}

let debouncedFn: any
const DEBOUNCE_INTERVAL = 300

const onChange = (
  values: Cinch.FilterFormData,
  dispatch: Dispatch<any>,
  _allValues: Partial<Cinch.FilterFormData>,
  previousValues: Partial<Cinch.FilterFormData>,
) => {
  if (debouncedFn) {
    debouncedFn.cancel()
  }

  const fetchListing = () => {
    const urlParams = getAllUrlParams()
    const params = { ...values }

    if (params.pageSize && params.pageSize > 24) {
      params.pageSize = 24
    }

    if (!params.make) {
      params.selectedModel = ''
    }

    // only get vehicles on initial page load or when form has NOT been updated via search
    const isSearchOnPageLoad = !previousValues.query && !!params.query

    const isSearchViaMainSearch =
      // eslint-disable-next-line no-prototype-builtins
      urlParams && Object.keys(urlParams).length === 1 && !!urlParams.hasOwnProperty('query')

    if (isSearchOnPageLoad || isSearchViaMainSearch) {
      dispatch(vehicleActions.search(params.query!))
    }

    const isNotSearchCausedByBrowserTimeout = !params.query || previousValues.query === params.query

    if (isNotSearchCausedByBrowserTimeout) {
      dispatch(vehicleActions.getListings(params, true))
    }
  }

  debouncedFn = debounce(fetchListing, DEBOUNCE_INTERVAL)
  debouncedFn()
}

export const mapStateToProps = (state: any) => {
  const formSelector = formValueSelector(FORM_NAME)
  const selectedMake = formSelector(state, 'make') || ''
  const moneyData = getMoney(state)
  const locationData = getLocation(state)
  const deliveryOption = getDelivery(state)
  const vehiclesFromState = Object.values(getFullListings(state))

  let models: Array<{ label: string; value: string }> = []

  if (selectedMake) {
    const mm = MakesModels.find((m) => m.Make === selectedMake)

    if (mm && mm.ModelList) {
      models = mm.ModelList.map((model) => ({ label: getModel(selectedMake, model), value: model }))
    }
  }

  return { models, selectedMake, moneyData, locationData, deliveryOption, vehiclesFromState }
}

export const mapDispatchToProps = (dispatch: Dispatch): FiltersPropsFromDispatch => ({
  updateSortCriteria: (value) => dispatch(change(FORM_NAME, 'sortingCriteria', value)),
  updateUserProfile: (values) => dispatch(updateProfile(values)),
  resetMoney: () => dispatch(updateProfile({ budget: 0 })),
  resetLocation: () => {
    dispatch(updateProfile({ location: '', longitude: null, latitude: null, distance: 0, town: '', postcode: '' }))
    dispatch(change(FORM_NAME, 'sortingCriteria', initialValues.sortingCriteria))
  },
  resetDelivery: () => dispatch(updateProfile({ deliveryOption: DELIVERY_OPTION.DEALERSHIP })),
  resetDistance: () => dispatch(updateProfile({ distance: 0 })),
  clearFilters: () => dispatch(initialize(FORM_NAME, { ...initialValues, pageNumber: 1 })),
})

const Form = reduxForm<Cinch.FilterFormData, any>({
  form: FORM_NAME,
  onChange,
  destroyOnUnmount: false,
  initialValues,
})(ListingFiltersWrapper)

export default connect(mapStateToProps, mapDispatchToProps)(Form)
